import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Gauge, Thermometer, Wind, Ruler } from "lucide-react";
import { motion } from "framer-motion";
import Logo from "../assets/Logo.png";

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // ✅ Fetch single product (no status check)
  useEffect(() => {
    axios
      .get(`https://api.microwellindustries.com/api/products/slug/${slug}`)
      .then((res) => setProduct(res.data))
      .catch((err) => console.error("Error fetching product:", err));
  }, [slug]);

  if (!product) {
    return (
      <div className="p-10 text-center text-gray-500 text-lg">Loading...</div>
    );
  }

  return (
    <div className="ml-64 bg-white min-h-screen px-4 md:px-8 py-10 max-w-7xl mx-auto">
      {/* Back to Dashboard + Title */}
      <motion.div
        className="mb-5 flex items-center justify-between"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <button
          onClick={() => navigate("/dashboard")}
          className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
        >
          ← Back to Dashboard
        </button>
      </motion.div>
      <h1 className="text-4xl md:text-4xl font-bold text-gray-900 mb-2">
          {product.name}
        </h1>
      <h1 className="text-3xl md:text-lg text-blue-700 mb-2">
        TYPE : {product.type}
      </h1>

      {/* Application */}
      {product.application && (
        <p className="text-lg text-gray-600 leading-relaxed text-justify bg-gray-50 p-6 rounded-xl shadow-sm border w-full mb-12">
          {product.application}
        </p>
      )}

      {/* Product Image(s) + Operating Limits */}
      <motion.div
        className="grid lg:grid-cols-2 gap-12 mb-20 items-center"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
      >
        {/* Product Image Card */}
        <div className="flex justify-center">
          <motion.div
            className="relative rounded-2xl shadow-lg border w-full max-w-md flex items-center justify-center overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            {/* Background Triangle */}
            <div className="absolute inset-0 bg-white">
              <div
                className="absolute inset-0"
                style={{
                  clipPath: "polygon(0% 100%, 0% 40%, 120% 100%)",
                  backgroundColor: "#1e3a8a", // Tailwind blue-900
                }}
              ></div>
            </div>

            {/* Watermark */}
            <img
              src={Logo}
              alt="Falcon Sealing Technology"
              className="absolute top-3 left-3 w-28 opacity-90"
            />

            {/* Product Image */}
            <img
              src={product.images?.[0] || product.image || "/placeholder.jpg"}
              alt={product.name}
              className="relative z-10 w-full h-80 object-contain p-6"
            />
          </motion.div>
        </div>

        {/* Operating Limits */}
        {(product.operatingLimits ||
          product.operatingLimits?.shaftDia ||
          product.operatingLimits?.pressure ||
          product.operatingLimits?.temperature ||
          product.operatingLimits?.speed ||
          product.operatingLimits?.tsPressureLimit20 ||
          product.operatingLimits?.tsPressureLimit40) && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Operating Limits
            </h2>
            <div className="space-y-4">
              {product.operatingLimits?.shaftDia && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Ruler className="w-5 h-5 text-blue-600" />
                  <span>
                    <strong>Shaft Dia:</strong>{" "}
                    {product.operatingLimits.shaftDia}
                  </span>
                </div>
              )}
              {product.operatingLimits?.pressure && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Gauge className="w-5 h-5 text-indigo-600" />
                  <span>
                    <strong>Pressure:</strong>{" "}
                    {product.operatingLimits.pressure}
                  </span>
                </div>
              )}
              {product.operatingLimits?.temperature && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Thermometer className="w-5 h-5 text-red-500" />
                  <span>
                    <strong>Temperature:</strong>{" "}
                    {product.operatingLimits.temperature}
                  </span>
                </div>
              )}
              {product.operatingLimits?.speed && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Wind className="w-5 h-5 text-green-600" />
                  <span>
                    <strong>Speed:</strong> {product.operatingLimits.speed}
                  </span>
                </div>
              )}
              {product.operatingLimits?.tsPressureLimit20 && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Gauge className="w-5 h-5 text-blue-600" />
                  <span>
                    <strong>TS 20 Pressure Limit:</strong> {product.operatingLimits.tsPressureLimit20}
                  </span>
                </div>
              )}
              {product.operatingLimits?.tsPressureLimit40 && (
                <div className="flex items-center gap-3 text-gray-700">
                  <Gauge className="w-5 h-5 text-indigo-600" />
                  <span>
                    <strong>TS 40 Pressure Limit:</strong> {product.operatingLimits.tsPressureLimit40}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      {/* Materials of Construction + Second Image */}
      <motion.div
        className="grid lg:grid-cols-2 gap-12 mb-20"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        viewport={{ once: true }}
      >
        {/* Materials */}
        {product.materials && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Materials
            </h2>
            <div className="border rounded-lg overflow-hidden">
              <dl className="divide-y divide-gray-200">
                {product.materials.sealRingFaces && (
                  <div className="px-4 py-3 bg-gray-50 flex justify-between">
                    <dt className="font-medium text-gray-700">
                      Seal Ring Faces
                    </dt>
                    <dd className="text-gray-600">
                      {product.materials.sealRingFaces}
                    </dd>
                  </div>
                )}
                {product.materials.seatFaces && (
                  <div className="px-4 py-3 flex justify-between">
                    <dt className="font-medium text-gray-700">Seat Faces</dt>
                    <dd className="text-gray-600">
                      {product.materials.seatFaces}
                    </dd>
                  </div>
                )}
                {product.materials.elastomer && (
                  <div className="px-4 py-3 bg-gray-50 flex justify-between">
                    <dt className="font-medium text-gray-700">Elastomer</dt>
                    <dd className="text-gray-600">
                      {product.materials.elastomer}
                    </dd>
                  </div>
                )}
                {product.materials.moc && (
                  <div className="px-4 py-3 flex justify-between">
                    <dt className="font-medium text-gray-700">MOC</dt>
                    <dd className="text-gray-600">{product.materials.moc}</dd>
                  </div>
                )}
                {product.materials.bellowMoc && (
                  <div className="px-4 py-3 bg-gray-50 flex justify-between">
                    <dt className="font-medium text-gray-700">Bellow MOC</dt>
                    <dd className="text-gray-600">
                      {product.materials.bellowMoc}
                    </dd>
                  </div>
                )}
                {product.materials.endFittingMoc && (
                  <div className="px-4 py-3 flex justify-between">
                    <dt className="font-medium text-gray-700">
                      End Fitting MOC
                    </dt>
                    <dd className="text-gray-600">
                      {product.materials.endFittingMoc}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        )}

        {/* Second Image */}
        {product.image2 && (
          <div className="flex justify-center">
            <img
              src={product.image2}
              alt="Second view"
              loading="lazy"
              className="w-80 h-80 object-contain rounded-lg border shadow-sm"
            />
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ProductDetail;
